import { skipToken, useSuspenseQuery } from "@apollo/client";
import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { wasteGeneratorStatusToLongLabel } from "encamp-shared/src/waste";
import { gql } from "generated-graphql";
import {
  FacilityWasteView,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { useEffect, useMemo } from "react";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { useGeneratorStatusSummary } from "routes/Customer/Waste/Facilities/Facility/useGeneratorStatusSummary";
import { useWasteFacility } from "routes/Customer/Waste/Facilities/Facility/useWasteFacility";
import { prettyPrintMonthYear } from "util/dates";

import { RecommendationHint } from "./RecommendationHint";
import {
  applicableStateStatusOptions,
  computeFieldDrift,
  FEDERAL_STATUS_OPTIONS,
  GeneratorStatusRecommendation,
  highestCalculatedFederalStatus,
  highestCalculatedStateStatus,
  parseFederalStatus,
  parseStateStatus,
  resolveChosenField,
  SavedWasteActivity,
  WasteActivityEdits,
} from "./wasteActivitySelections";

/**
 * Window-bound calculated generator status: the server unifies vendor +
 * e‑Manifest generation across the requested window and returns the highest
 * calculated federal status. Bounded by the step's activity window (not the
 * calendar year) and based on all hazardous generation (`view: ALL`), since
 * generator status is a total-generation threshold independent of reportability.
 *
 * `byMonth.calculatedStateGeneratorStatus` feeds the STATE recommendation: the
 * highest-ranked CalculatedRegulatoryStatus-derived state status over the
 * window, mirroring the snapshot gatherer's fallback (NOT the EPA-reported
 * state status, which stays a display-only context row).
 */
const CALCULATED_GENERATOR_STATUS = gql(`
  query WasteActivityCalculatedGeneratorStatus(
    $facilityId: ID!
    $filter: FacilityWasteShipmentsFilter!
  ) {
    facilityWasteShipmentCharts(facilityId: $facilityId, filter: $filter) {
      wasteShipmentsByMonth {
        byMonth {
          month
          calculatedFederalGeneratorStatus
          calculatedStateGeneratorStatus
        }
      }
    }
  }
`);

/**
 * Generator status for the Waste Activity step. The EPA's currently-reported
 * statuses (with as-of dates) remain read-only context rows; the federal and
 * state generator statuses filed on this submission are editable selects,
 * prefilled from the saved `SubmissionWasteActivity` row when one exists and
 * from the live recommendation (Encamp's calculated status over the step's
 * activity window) otherwise.
 *
 * Sourced facility-scoped (no `WasteFacilityContext`): facility `state` via
 * `useWasteFacility`, reported status via `useGeneratorStatusSummary`, and the
 * calculated statuses via `facilityWasteShipmentCharts` — all bounded to the
 * activity window.
 *
 * The live recommendation this component displays is reported up via
 * `onRecommendation` so the step's save captures exactly what the user saw.
 */
export function WasteGeneratorStatus({
  facilityId,
  startDate,
  endDate,
  saved,
  edits,
  onEdit,
  onRecommendation,
  readOnly,
}: {
  facilityId: string;
  startDate: string;
  endDate: string;
  saved: SavedWasteActivity | null;
  edits: WasteActivityEdits;
  onEdit: (edit: WasteActivityEdits) => void;
  onRecommendation: (recommendation: GeneratorStatusRecommendation) => void;
  readOnly: boolean;
}) {
  const { data: facilityData } = useWasteFacility(facilityId);
  const { data: summaryData } = useGeneratorStatusSummary(
    facilityId,
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null
  );
  const { data: chartsData } = useSuspenseQuery(
    CALCULATED_GENERATOR_STATUS,
    startDate && endDate
      ? {
          variables: {
            facilityId,
            filter: { startDate, endDate, view: FacilityWasteView.All },
          },
        }
      : skipToken
  );

  const facility = facilityData?.facility;
  const state = facility?.state ?? null;
  const reported = summaryData?.generatorStatusSummary?.currentlyReportedStatus;

  const shipmentsByMonth = chartsData?.facilityWasteShipmentCharts?.wasteShipmentsByMonth;

  // Live recommendations, from the same month rows rendered below.
  const recommendedFederal = useMemo(
    () =>
      highestCalculatedFederalStatus(
        (shipmentsByMonth?.byMonth ?? []).map((month) => month.calculatedFederalGeneratorStatus)
      ),
    [shipmentsByMonth]
  );
  const recommendedState = useMemo(
    () =>
      highestCalculatedStateStatus(
        (shipmentsByMonth?.byMonth ?? []).map((month) => month.calculatedStateGeneratorStatus),
        state
      ),
    [shipmentsByMonth, state]
  );

  // Report the displayed recommendation up for save-time capture. Re-fires when
  // the activity window changes the calculated statuses. Only once the charts
  // query actually ran (it is skipToken-skipped without a bounded window,
  // mirroring the codes side): the step's Save stays disabled rather than
  // capturing a hollow null recommendation as "what the user was shown".
  //
  // The lint's alternative (parent owns the query, passes data down) would
  // decouple the save payload from this render: the design requires the saved
  // recommendation to be exactly what this section displayed, so the section
  // that renders it reports it.
  useEffect(() => {
    if (!chartsData) {
      return;
    }
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    onRecommendation({
      federalGeneratorStatus: recommendedFederal,
      stateGeneratorStatus: recommendedState,
    });
  }, [chartsData, onRecommendation, recommendedFederal, recommendedState]);

  const hasSaved = saved != null;
  const chosenFederal = resolveChosenField(
    edits.federalGeneratorStatus,
    saved?.federalGeneratorStatus ?? null,
    hasSaved,
    recommendedFederal
  );
  const chosenState = resolveChosenField(
    edits.stateGeneratorStatus,
    saved?.stateGeneratorStatus ?? null,
    hasSaved,
    recommendedState
  );

  const federalDrift = computeFieldDrift({
    chosen: chosenFederal,
    savedRecommended: saved?.recommendedFederalGeneratorStatus ?? null,
    liveRecommended: recommendedFederal,
    hasSaved,
  });
  const stateDrift = computeFieldDrift({
    chosen: chosenState,
    savedRecommended: saved?.recommendedStateGeneratorStatus ?? null,
    liveRecommended: recommendedState,
    hasSaved,
  });

  // The reported status renders on its own line beneath the field label (with
  // an optional "as of {date}" caption).
  const renderStatusValue = (
    status: RcraInfoFederalWasteGeneratorStatus | StateRegulatoryStatus | null | undefined,
    date: string | null | undefined
  ) => (
    <Stack spacing={0}>
      <Typography variant="body2">
        {status ? wasteGeneratorStatusToLongLabel(status) : "—"}
      </Typography>
      {date && (
        <Typography variant="caption" color="text.secondary">
          as of {prettyPrintMonthYear(date)}
        </Typography>
      )}
    </Stack>
  );

  // Federal then state, vertically stacked (matching the Reportable Waste Codes
  // section) so each status gets a full-width input.
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        {/* Plain subtitle2 (no 600 weight): these are sub-group labels under the
            "Generator Status" StepSubHeader, so they match the waste codes
            section's subtitle2 field labels instead of competing with the
            600-weight section header one level up. */}
        <Typography variant="subtitle2">Federal</Typography>
        <DisplayOnlyField
          label="EPA reported status"
          renderValue={() =>
            renderStatusValue(
              reported?.fedWasteGenerator?.status,
              reported?.fedWasteGenerator?.createdDate
            )
          }
        />
        <Stack spacing={0.5}>
          <TextField
            select
            size="small"
            fullWidth
            label="Federal generator status"
            value={chosenFederal ?? ""}
            disabled={readOnly}
            onChange={(event) =>
              onEdit({ federalGeneratorStatus: parseFederalStatus(event.target.value) })
            }
          >
            {/* Empty option so a chosen status can be cleared back to null —
                an explicit "no status" edit the model persists as null. */}
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {FEDERAL_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {wasteGeneratorStatusToLongLabel(status)}
              </MenuItem>
            ))}
          </TextField>
          <RecommendationHint
            recommendationText={
              recommendedFederal ? wasteGeneratorStatusToLongLabel(recommendedFederal) : null
            }
            drift={federalDrift}
            onAccept={() => onEdit({ federalGeneratorStatus: recommendedFederal })}
            readOnly={readOnly}
          />
        </Stack>
      </Stack>
      {state && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">State</Typography>
          <DisplayOnlyField
            label="EPA reported status"
            renderValue={() =>
              renderStatusValue(
                reported?.stateWasteGenerator?.status,
                reported?.stateWasteGenerator?.createdDate
              )
            }
          />
          <Stack spacing={0.5}>
            <TextField
              select
              size="small"
              fullWidth
              label="State generator status"
              value={chosenState ?? ""}
              disabled={readOnly}
              onChange={(event) =>
                onEdit({ stateGeneratorStatus: parseStateStatus(event.target.value) })
              }
            >
              {/* Empty option so a chosen status can be cleared back to null —
                  an explicit "no status" edit the model persists as null. */}
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {/* Filtered to statuses applicable in the facility's state, plus
                  the current value if it falls outside that list (so legacy or
                  out-of-state saved data is never silently blanked). */}
              {applicableStateStatusOptions(state, chosenState).map((status) => (
                <MenuItem key={status} value={status}>
                  {wasteGeneratorStatusToLongLabel(status)}
                </MenuItem>
              ))}
            </TextField>
            <RecommendationHint
              recommendationText={
                recommendedState ? wasteGeneratorStatusToLongLabel(recommendedState) : null
              }
              drift={stateDrift}
              onAccept={() => onEdit({ stateGeneratorStatus: recommendedState })}
              readOnly={readOnly}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
